import {
  CREATE_ORDER_TIMEOUT_MS,
  isMutationTimeoutError,
} from './unwrapMutationWithTimeout'
import { sendCreateOrderErrorToTelegram } from './sendCreateOrderErrorToTelegram'

export const CREATE_ORDER_ERROR_CODES = {
  TIMEOUT: 'TIMEOUT',
  NETWORK: 'NETWORK',
  BAD_REQUEST: 'BAD_REQUEST',
  DATA_REQUIRED: 'DATA_REQUIRED',
  ORDER_SAVE_FAILED: 'ORDER_SAVE_FAILED',
  ORDER_TOO_LARGE: 'ORDER_TOO_LARGE',
  ORDER_PAYLOAD_INVALID: 'ORDER_PAYLOAD_INVALID',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  HTTP: 'HTTP',
  PARSE: 'PARSE',
  UNKNOWN: 'UNKNOWN',
}

function pickServerPayload(err) {
  const data = err?.data
  if (!data || typeof data !== 'object') {
    return { serverError: null, serverCode: null, serverDetail: null }
  }
  return {
    serverError:
      typeof data.error === 'string'
        ? data.error
        : typeof data.message === 'string'
          ? data.message
          : null,
    serverCode: typeof data.code === 'string' ? data.code : null,
    serverDetail: typeof data.detail === 'string' ? data.detail : null,
  }
}

function userMessageFor(code, status, serverError) {
  switch (code) {
    case CREATE_ORDER_ERROR_CODES.TIMEOUT:
      return `El servidor no respondió a tiempo (${Math.round(CREATE_ORDER_TIMEOUT_MS / 1000)}s). Inténtalo de nuevo.`
    case CREATE_ORDER_ERROR_CODES.NETWORK:
      return 'No hay conexión con el servidor. Comprueba internet e inténtalo de nuevo.'
    case CREATE_ORDER_ERROR_CODES.DATA_REQUIRED:
      return 'Faltan datos del pedido. Recarga la página e inténtalo de nuevo.'
    case CREATE_ORDER_ERROR_CODES.BAD_REQUEST:
      return serverError
        ? `Pedido no válido: ${serverError}`
        : 'Pedido no válido. Revisa los datos e inténtalo de nuevo.'
    case CREATE_ORDER_ERROR_CODES.ORDER_SAVE_FAILED:
      return 'No se pudo guardar el pedido en el servidor. Inténtalo de nuevo.'
    case CREATE_ORDER_ERROR_CODES.ORDER_TOO_LARGE:
      return 'El pedido es demasiado grande para guardarlo. Reduce artículos e inténtalo de nuevo.'
    case CREATE_ORDER_ERROR_CODES.ORDER_PAYLOAD_INVALID:
      return 'Los datos del pedido no son válidos. Recarga la página e inténtalo de nuevo.'
    case CREATE_ORDER_ERROR_CODES.INTERNAL_ERROR:
      return 'Error interno del servidor al crear el pedido. Inténtalo de nuevo.'
    case CREATE_ORDER_ERROR_CODES.PARSE:
      return 'Respuesta incorrecta del servidor. Inténtalo de nuevo.'
    case CREATE_ORDER_ERROR_CODES.HTTP:
      return status
        ? `Error del servidor (HTTP ${status}). Inténtalo de nuevo.`
        : 'Error del servidor. Inténtalo de nuevo.'
    default:
      return 'No se pudo crear el pedido. Inténtalo de nuevo.'
  }
}

/**
 * Normalize RTK Query / timeout / plain Error into a support-friendly shape.
 */
export function classifyCreateOrderError(err) {
  if (isMutationTimeoutError(err)) {
    const code = CREATE_ORDER_ERROR_CODES.TIMEOUT
    return {
      code,
      kind: 'timeout',
      status: null,
      serverError: null,
      serverCode: null,
      serverDetail: null,
      userMessage: userMessageFor(code),
      technicalMessage: err?.message || 'CREATE_ORDER_TIMEOUT',
    }
  }

  const status = err?.status
  const { serverError, serverCode, serverDetail } = pickServerPayload(err)

  if (status === 'FETCH_ERROR') {
    const code = CREATE_ORDER_ERROR_CODES.NETWORK
    return {
      code,
      kind: 'network',
      status,
      serverError,
      serverCode,
      serverDetail,
      userMessage: userMessageFor(code),
      technicalMessage: err?.error || 'FETCH_ERROR',
    }
  }

  if (status === 'PARSING_ERROR') {
    const code = CREATE_ORDER_ERROR_CODES.PARSE
    return {
      code,
      kind: 'parse',
      status,
      serverError,
      serverCode,
      serverDetail,
      userMessage: userMessageFor(code),
      technicalMessage: err?.error || 'PARSING_ERROR',
    }
  }

  if (typeof status === 'number') {
    let code = CREATE_ORDER_ERROR_CODES.HTTP
    if (serverCode === 'DATA_REQUIRED' || status === 400) {
      code =
        serverCode === 'DATA_REQUIRED' || /data is required/i.test(serverError || '')
          ? CREATE_ORDER_ERROR_CODES.DATA_REQUIRED
          : CREATE_ORDER_ERROR_CODES.BAD_REQUEST
    } else if (
      serverCode === 'ORDER_TOO_LARGE' ||
      /too large/i.test(serverDetail || serverError || '')
    ) {
      code = CREATE_ORDER_ERROR_CODES.ORDER_TOO_LARGE
    } else if (serverCode === 'ORDER_PAYLOAD_INVALID') {
      code = CREATE_ORDER_ERROR_CODES.ORDER_PAYLOAD_INVALID
    } else if (
      serverCode === 'ORDER_SAVE_FAILED' ||
      /Ошибка сохранения заказа/i.test(serverError || '')
    ) {
      code = CREATE_ORDER_ERROR_CODES.ORDER_SAVE_FAILED
    } else if (serverCode === 'INTERNAL_ERROR' || status >= 500) {
      code =
        serverCode === 'ORDER_SAVE_FAILED'
          ? CREATE_ORDER_ERROR_CODES.ORDER_SAVE_FAILED
          : CREATE_ORDER_ERROR_CODES.INTERNAL_ERROR
    }

    return {
      code,
      kind: 'http',
      status,
      serverError,
      serverCode,
      serverDetail,
      userMessage: userMessageFor(code, status, serverError),
      technicalMessage:
        serverDetail ||
        serverError ||
        err?.error ||
        `HTTP ${status}`,
    }
  }

  if (err instanceof Error) {
    const code = CREATE_ORDER_ERROR_CODES.UNKNOWN
    return {
      code,
      kind: 'unknown',
      status: null,
      serverError: null,
      serverCode: null,
      serverDetail: null,
      userMessage: userMessageFor(code),
      technicalMessage: err.message,
    }
  }

  const code = CREATE_ORDER_ERROR_CODES.UNKNOWN
  return {
    code,
    kind: 'unknown',
    status: status ?? null,
    serverError,
    serverCode,
    serverDetail,
    userMessage: userMessageFor(code),
    technicalMessage: String(err?.message || err?.error || err || 'unknown'),
  }
}

export function formatCreateOrderErrorAlert(classified) {
  const info = classified?.code
    ? classified
    : classifyCreateOrderError(classified)
  const lines = [info.userMessage, '', `Código: ${info.code}`]
  if (info.status != null) lines.push(`HTTP: ${info.status}`)
  if (info.serverCode) lines.push(`Servidor: ${info.serverCode}`)
  if (info.technicalMessage && info.technicalMessage !== info.userMessage) {
    lines.push(`Detalle: ${info.technicalMessage}`)
  }
  return lines.join('\n')
}

export function logCreateOrderError(err, context = 'createOrderTPO') {
  const classified = classifyCreateOrderError(err)
  console.error(`[${context}] ${classified.code}`, {
    ...classified,
    raw: err,
  })
  return classified
}

export function reportCreateOrderFailure(
  err,
  context = 'createOrderTPO',
  meta = {},
) {
  const classified = logCreateOrderError(err, context)
  void sendCreateOrderErrorToTelegram(classified, context, {
    href: typeof window !== 'undefined' ? window.location.href : undefined,
    ...meta,
  })
  alert(formatCreateOrderErrorAlert(classified))
  return classified
}
